import * as React from 'react';
import { styled } from '@mentimeter/ragnar-styled';
import { cn } from '@mentimeter/ragnar-tailwind-config';
import { useTranslations } from 'use-intl';
import { rule as boxRule } from '../box';
import { ScreenReaderOnly } from '../screenreader-only';
import {
  characterCount,
  type CharacterCountVariantProps,
} from './CharacterCount.variants';

export interface CharacterCountT
  extends Pick<CharacterCountVariantProps, 'multiline' | 'iconTrailing'> {
  value: string;
  maxLength: number;
  id?: string | undefined;
  className?: string | undefined;
}

export const stringToCharacterCount = (value: string) => {
  return value?.length;
};

const CharacterCountC = ({
  value = '',
  maxLength,
  className,
  id,
  multiline,
  iconTrailing,
}: CharacterCountT) => {
  const t = useTranslations('ragnar.character_count');
  const charactersTyped = stringToCharacterCount(value);
  const percentage = charactersTyped / maxLength;
  const count = maxLength - charactersTyped;
  const color = percentage > 0.5 ? 'negative' : 'default';
  const [liveCount, setLiveCount] = React.useState(count);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setLiveCount(count < 0 ? 0 : count);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [count]);

  return (
    <span
      id={id}
      className={cn(
        characterCount({ color, iconTrailing, multiline }),
        className,
      )}
    >
      <span aria-hidden>{count < 0 ? 0 : count}</span>

      <ScreenReaderOnly aria-live="polite" aria-atomic>
        {t('count_characters_left_a11y', {
          count: liveCount.toString(),
        })}
      </ScreenReaderOnly>
    </span>
  );
};

export const CharacterCount = styled(CharacterCountC, {
  displayName: 'CharacterCount',
})(boxRule, 'kosmosSpacing');
